import type { World } from '@ebu/world-schema';
import type { AttemptStoreRecord, EvidenceStoreRecord } from '../../adapters/local-store/schema';
import { evidenceFor, type LearningAttempt } from '../discovery-game/learning-records';
import { connect, placeObject, setProps } from '../world-builder/editor-model';
import { starterWorld } from '../world-builder/starters';

/**
 * The two fictional sample learners (spec feature 9): "new to circuits" and "experienced with
 * circuits". They are not real children and have no names. Choosing one clones these seeds into a
 * new workspace on this browser (LocalStore.cloneSampleWorkspace), so every visitor gets a private
 * copy. Their attempts are real records for the real adaptive engine, so the Lab suggests
 * different challenges for each; their projects are real workshop worlds.
 */

export const SAMPLE_PROFILES = ['sample-new-to-circuits', 'sample-experienced'] as const;
export type SampleProfileId = (typeof SAMPLE_PROFILES)[number];

export const isSampleProfile = (id: string | null): id is SampleProfileId =>
  SAMPLE_PROFILES.includes(id as SampleProfileId);

type SeedAttempt = Omit<LearningAttempt, 'id' | 'missionId' | 'challengeVersion'>;

const MISSION = 'lighthouse-circuits';

const ATTEMPTS: Readonly<Record<SampleProfileId, readonly SeedAttempt[]>> = {
  'sample-new-to-circuits': [
    {
      challengeId: 'light-the-lamp',
      objectiveId: 'circuits.closed-loop',
      level: 1,
      outcome: 'not-yet',
      tests: 3,
      hintsUsed: 1,
      showMeUsed: false,
      chosenByChild: false,
      endedAt: '2026-10-20T15:10:00.000Z',
    },
    {
      challengeId: 'light-the-lamp',
      objectiveId: 'circuits.closed-loop',
      level: 1,
      outcome: 'solved',
      tests: 2,
      hintsUsed: 3,
      showMeUsed: true,
      chosenByChild: false,
      endedAt: '2026-10-20T15:18:00.000Z',
    },
  ],
  'sample-experienced': [
    {
      challengeId: 'two-bright-lamps',
      objectiveId: 'circuits.closed-loop',
      level: 2,
      outcome: 'solved',
      tests: 1,
      hintsUsed: 0,
      showMeUsed: false,
      chosenByChild: false,
      endedAt: '2026-10-18T14:02:00.000Z',
    },
    {
      challengeId: 'the-switch-decides',
      objectiveId: 'circuits.switch-control',
      level: 1,
      outcome: 'solved',
      tests: 1,
      hintsUsed: 0,
      showMeUsed: false,
      chosenByChild: false,
      endedAt: '2026-10-18T14:09:00.000Z',
    },
    {
      challengeId: 'a-switch-for-each-lamp',
      objectiveId: 'circuits.switch-control',
      level: 2,
      outcome: 'solved',
      tests: 2,
      hintsUsed: 1,
      showMeUsed: false,
      chosenByChild: false,
      endedAt: '2026-10-18T14:20:00.000Z',
    },
    {
      challengeId: 'find-the-broken-wire',
      objectiveId: 'circuits.fault-diagnosis',
      level: 1,
      outcome: 'solved',
      tests: 4,
      hintsUsed: 2,
      showMeUsed: false,
      chosenByChild: false,
      endedAt: '2026-10-21T16:05:00.000Z',
    },
  ],
};

export const START_CHOICE: Readonly<Record<SampleProfileId, 'beginning' | 'harder'>> = {
  'sample-new-to-circuits': 'beginning',
  'sample-experienced': 'harder',
};

/** The seeded attempts and their evidence (one record per idea), keyed to the new workspace. */
export function sampleLearning(
  profile: SampleProfileId,
  workspaceId: string,
): { attempts: AttemptStoreRecord[]; evidence: EvidenceStoreRecord[] } {
  const history: LearningAttempt[] = ATTEMPTS[profile].map((a, i) => ({
    ...a,
    id: `sample-${i + 1}-${workspaceId}`,
    missionId: MISSION,
    challengeVersion: 1,
  }));
  const lastPerIdea = new Map(history.map((a) => [a.objectiveId, a]));
  return {
    attempts: history.map((a) => ({ ...a, workspaceId })),
    evidence: [...lastPerIdea.values()].map((a) => evidenceFor(workspaceId, a, history)),
  };
}

function step(world: World, change: (w: World) => World | string): World {
  const next = change(world);
  if (typeof next === 'string') throw new Error(`Sample project step refused: ${next}`);
  return next;
}

/** Real workshop projects for each profile; titles come from the UI language. */
export function sampleWorlds(
  profile: SampleProfileId,
  texts: { firstRoom: string; nightLight: string; posterCaption: string },
): World[] {
  if (profile === 'sample-new-to-circuits') {
    let w = starterWorld('empty', {
      id: 'sample-room',
      title: texts.firstRoom,
      now: new Date('2026-10-20T15:30:00.000Z'),
      posterCaption: texts.posterCaption,
    });
    w = step(w, placeObject('rug', [0, 0, 0], true));
    w = step(w, placeObject('table', [0, 0, -1], true));
    w = step(w, placeObject('battery', [-1, 0, 1], true));
    w = step(w, placeObject('lamp', [1, 0, 1], true));
    // One wire so far: the loop is not closed yet, so the lamp is off.
    w = step(
      w,
      connect({ objectId: 'battery-1', port: 'positive' }, { objectId: 'lamp-1', port: 'a' }),
    );
    return [{ ...w, updatedAt: '2026-10-20T15:42:00.000Z' }];
  }
  let w = starterWorld('lighthouse', {
    id: 'sample-night-light',
    title: texts.nightLight,
    now: new Date('2026-10-21T16:30:00.000Z'),
    posterCaption: texts.posterCaption,
  });
  // The switch is swapped for a light sensor: the lamp turns on by itself at night.
  w = { ...w, objects: w.objects.filter((o) => o.id !== 'switch-1') };
  w = step(w, placeObject('light-sensor', [0, 0.8, 0.3], true));
  w = step(w, setProps('light-sensor-1', { threshold: 0.4 }));
  for (const [from, to] of [
    [
      { objectId: 'battery-1', port: 'positive' },
      { objectId: 'light-sensor-1', port: 'in' },
    ],
    [
      { objectId: 'light-sensor-1', port: 'out' },
      { objectId: 'lamp-1', port: 'a' },
    ],
    [
      { objectId: 'lamp-1', port: 'b' },
      { objectId: 'battery-1', port: 'negative' },
    ],
  ] as const) {
    w = step(w, connect(from, to));
  }
  return [{ ...w, updatedAt: '2026-10-21T16:48:00.000Z' }];
}
